/* VastraGharr Luxe — collection tabs <vg-collection-tabs> (v3)
   - Sliding gold underline ("ink") under the active tab; header "View all" follows the active tab.
   - WAI-ARIA tabs: arrows / Home / End move and select, focus stays on the tab row.
   - Panels 2+ arrive as inert <template>s and are instantiated on demand (tab chosen, tab hovered or
     focused), or one per idle moment once the section is near the viewport. New content is handed to
     the shared utilities (image skeletons, reveal) and announced with `vg:tabs:panel-ready`.
   - Theme editor: block select switches tab instantly; section load/unload rebuild cleanly. */
(() => {
  if (customElements.get('vg-collection-tabs')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const idle = (callback) =>
    'requestIdleCallback' in window
      ? window.requestIdleCallback(callback, { timeout: 2500 })
      : window.setTimeout(() => callback({ timeRemaining: () => 8, didTimeout: true }), 400);
  const cancelIdle = (id) => {
    if ('cancelIdleCallback' in window) window.cancelIdleCallback(id);
    else window.clearTimeout(id);
  };

  class VgCollectionTabs extends HTMLElement {
    connectedCallback() {
      this.tablist = this.querySelector('[data-vg-tablist]');
      if (!this.tablist) {
        // Single tab: nothing to switch, but a lone template panel still needs to render.
        this.querySelectorAll('[data-vg-panel]').forEach((panel) => this.ensurePanel(panel));
        return;
      }

      this.tabs = Array.from(this.tablist.querySelectorAll('[role="tab"]'));
      this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
      this.ink = this.tablist.querySelector('.vg-ctabs__ink');
      this.viewAll = this.querySelector('.vg-section-header__link');
      this.activeIndex = Math.max(
        0,
        this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
      );

      this.onClick = (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (!tab || !this.tablist.contains(tab)) return;
        this.select(this.tabs.indexOf(tab), { focus: false });
      };

      this.onKeydown = (event) => {
        const current = this.tabs.indexOf(document.activeElement);
        if (current === -1) return;
        const rtl = getComputedStyle(this).direction === 'rtl';
        const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
        const backward = rtl ? 'ArrowRight' : 'ArrowLeft';
        let next = null;

        if (event.key === forward) next = (current + 1) % this.tabs.length;
        else if (event.key === backward) next = (current - 1 + this.tabs.length) % this.tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.tabs.length - 1;

        if (next === null) return;
        event.preventDefault();
        this.select(next, { focus: true });
      };

      // Hover / keyboard focus on a tab: build its panel ahead of the click.
      this.onIntent = (event) => {
        const tab = event.target.closest && event.target.closest('[role="tab"]');
        if (!tab || !this.tablist.contains(tab)) return;
        this.ensurePanel(this.panels[this.tabs.indexOf(tab)]);
      };

      this.onBlockSelect = (event) => {
        const tab = event.target && event.target.closest ? event.target.closest('[role="tab"]') : null;
        if (!tab || !this.tablist.contains(tab)) return;
        this.select(this.tabs.indexOf(tab), { focus: false, instant: true });
      };

      this.onResize = () => this.moveInk(true);

      this.tablist.addEventListener('click', this.onClick);
      this.tablist.addEventListener('keydown', this.onKeydown);
      this.tablist.addEventListener('pointerover', this.onIntent, { passive: true });
      this.tablist.addEventListener('focusin', this.onIntent);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      if (this.ink) {
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.onResize);
          this.resizeObserver.observe(this.tablist);
        } else {
          window.addEventListener('resize', this.onResize);
        }
        // Web fonts change tab widths once they load
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.moveInk(true));
        this.moveInk(true);
        this.tablist.classList.add('has-ink');
      }

      this.ensurePanel(this.panels[this.activeIndex]);
      this.scrollTabIntoView(this.tabs[this.activeIndex], true);
      this.watchForPrewarm();
    }

    disconnectedCallback() {
      if (this.prewarmObserver) this.prewarmObserver.disconnect();
      if (this.idleId) cancelIdle(this.idleId);
      this.idleId = null;
      if (!this.tablist) return;
      this.tablist.removeEventListener('click', this.onClick);
      this.tablist.removeEventListener('keydown', this.onKeydown);
      this.tablist.removeEventListener('pointerover', this.onIntent);
      this.tablist.removeEventListener('focusin', this.onIntent);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.onResize);
    }

    select(index, { focus = false, instant = false } = {}) {
      if (index < 0 || index >= this.tabs.length) return;
      const changed = index !== this.activeIndex;
      const panel = this.panels[index];
      this.ensurePanel(panel);

      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
        tab.classList.toggle('is-active', active);
      });

      this.panels.forEach((item, i) => {
        if (!item) return;
        const active = i === index;
        item.hidden = !active;
        item.classList.toggle('is-active', active);
        if (active && changed && !instant && !reducedMotion.matches) {
          item.classList.remove('is-entering');
          void item.offsetWidth; // restart the entrance animation
          item.classList.add('is-entering');
        }
      });

      this.activeIndex = index;
      const tab = this.tabs[index];
      if (focus) tab.focus({ preventScroll: true });
      this.scrollTabIntoView(tab, instant);
      this.moveInk(instant);
      this.updateViewAll(tab);

      if (panel && changed) {
        this.refreshSliders(panel);
        this.dispatchEvent(new CustomEvent('vg:tabs:change', { bubbles: true, detail: { index, panel, tab } }));
      }
    }

    /* Turn a panel's inert <template> into live content (once). */
    ensurePanel(panel) {
      if (!panel) return false;
      const template = Array.from(panel.children).find((child) => child.matches('template[data-vg-panel-template]'));
      if (!template) return false;
      const content = document.importNode(template.content, true);
      template.replaceWith(content);

      // Hidden-panel content never needs to fade in; show it as it is and let skeletons track its images.
      const VG = window.VG;
      if (VG && VG.reveal && typeof VG.reveal.revealAll === 'function') VG.reveal.revealAll(panel);
      if (VG && VG.images && typeof VG.images.scan === 'function') VG.images.scan(panel);
      if (!panel.hidden) this.refreshSliders(panel);

      this.dispatchEvent(new CustomEvent('vg:tabs:panel-ready', { bubbles: true, detail: { panel } }));
      return true;
    }

    /* Once the section is close, build the remaining panels one per idle moment (no long tasks). */
    watchForPrewarm() {
      const pending = () => this.panels.filter((panel) => panel && panel.querySelector(':scope > template[data-vg-panel-template]'));
      if (!pending().length) return;

      const step = () => {
        this.idleId = null;
        const next = pending()[0];
        if (!next || !this.isConnected) return;
        this.ensurePanel(next);
        if (pending().length) this.idleId = idle(step);
      };

      if (!('IntersectionObserver' in window)) {
        this.idleId = idle(step);
        return;
      }
      this.prewarmObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.prewarmObserver.disconnect();
          this.prewarmObserver = null;
          if (!this.idleId) this.idleId = idle(step);
        },
        { rootMargin: '600px 0px' }
      );
      this.prewarmObserver.observe(this);
    }

    /* Slide the gold underline under the active tab (offsets are relative to the tab row). */
    moveInk(instant) {
      if (!this.ink) return;
      const tab = this.tabs[this.activeIndex];
      if (!tab) return;
      const text = tab.querySelector('.vg-ctabs__tab-text') || tab;
      const tabRect = this.tablist.getBoundingClientRect();
      const textRect = text.getBoundingClientRect();
      if (!textRect.width) return; // hidden (e.g. collapsed in the editor, or not rendered yet)
      const left = textRect.left - tabRect.left + this.tablist.scrollLeft;
      if (instant) this.ink.classList.add('is-instant');
      this.ink.style.setProperty('--vg-ink-x', `${Math.round(left)}px`);
      this.ink.style.setProperty('--vg-ink-w', `${Math.round(textRect.width)}px`);
      if (instant) {
        void this.ink.offsetWidth;
        this.ink.classList.remove('is-instant');
      }
    }

    /* Point the header "View all" link at the active tab's collection. */
    updateViewAll(tab) {
      if (!this.viewAll || !tab) return;
      const url = tab.dataset.url;
      if (url) {
        this.viewAll.setAttribute('href', url);
        this.viewAll.hidden = false;
      } else {
        this.viewAll.hidden = true;
      }
    }

    /* A slider measured while its panel was hidden has zero width: ask it to re-measure. */
    refreshSliders(panel) {
      window.requestAnimationFrame(() => {
        panel.querySelectorAll('[data-vg-slider]').forEach((slider) => {
          ['update', 'refresh', 'updateButtons'].some((method) => {
            if (typeof slider[method] === 'function') {
              slider[method]();
              return true;
            }
            return false;
          });
          const track = slider.querySelector('[data-vg-slider-track]');
          if (track) track.dispatchEvent(new Event('scroll'));
        });
      });
    }

    /* Keep the active tab visible in the horizontally scrolling tab row on phones. */
    scrollTabIntoView(tab, instant) {
      if (!tab || this.tablist.scrollWidth <= this.tablist.clientWidth) return;
      const target = tab.offsetLeft - (this.tablist.clientWidth - tab.offsetWidth) / 2;
      this.tablist.scrollTo({
        left: Math.max(0, target),
        behavior: instant || reducedMotion.matches ? 'auto' : 'smooth',
      });
    }
  }

  customElements.define('vg-collection-tabs', VgCollectionTabs);
})();
